import React, { useCallback, useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Grid } from '@mui/material'
import HeaderTitle from '_components/header-title'
import { Button as ReferaButton } from '@refera/ui-web'
import Button from '_components/button'
import { navigate, useParams } from '@reach/router'
import { FormProvider, useForm } from 'react-hook-form'
import Svg from '_components/svg'
import EditIcon from '_assets/icons/ic-edit.svg'
import { useDialog } from '_/hooks/use-dialog'
import { getCities, getStates } from '_/modules/utils/actions'

import {
  BuildingData,
  BuildingAddress,
  Contact,
  Information,
  Units,
  ContactManager,
} from './building-accordions'
import { useToast } from '_/hooks/use-toast'

import {
  getBuildingTypes,
  postAgencyBuilding,
  getAgencyBuildingById,
  patchAgencyBuilding,
  getBuildingUnitsById,
  getAgencyFull,
} from '_/modules/agency/actions'
import { getBuildingTypesSelector, agencySelector } from '_/modules/agency/selectors'

import { includeDDI } from '_/views/new-service/utils/includeDDI'
import useStyles from './styles'

const AgencyBuilding = ({ isEditing = true, setIsEditing }) => {
  const styles = useStyles()
  const dispatch = useDispatch()
  const { agencyId, id } = useParams()
  const agency = useSelector(agencySelector)
  const buildingTypes = useSelector(getBuildingTypesSelector)
  const { showDialog, closeDialog } = useDialog()
  const { showToast } = useToast()

  const [isLoading, setIsLoading] = useState(false)
  const [buildingData, setBuildingData] = useState({})
  const [units, setUnits] = useState([])
  const [deletedUnits, setDeletedUnits] = useState([])

  const methods = useForm({
    mode: 'all',
    defaultValues: {
      name: '',
      buildingType: '',
      businessType: '',
      responsible: '',
      phone: '',
      emails: '',
      observations: '',
      active: true,
      cep: '',
      number: '',
      address: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      responsibleManager: '',
      phoneManager: '',
      emailsManager: '',
      cpfCnpjManager: '',
      isServiceOrderBuilding: true,
    },
  })

  const {
    handleSubmit,
    reset,
    formState: { dirtyFields, isValid },
  } = methods

  const handleBackNavigation = useCallback(() => {
    navigate(-1)
  }, [])

  const validate = data => {
    const type = buildingTypes.find(item => item?.id === data?.buildingType)
    if (!type?.acceptsMultipleUnits && units.length > 1) {
      showToast({
        type: 'error',
        message:
          'Este tipo de empreendimento só aceita uma única unidade. Apague as demais para salvar o registro.',
      })
      return false
    }
    return true
  }

  const onSubmit = useCallback(
    async data => {
      if (!validate(data)) return
      const transformedData = {
        ...data,
        agency: agencyId,
        unitsToDelete: deletedUnits?.map(u => u.id),
      }

      setIsLoading(true)
      const action = id
        ? patchAgencyBuilding(id, agencyId, transformedData)
        : postAgencyBuilding(agencyId, transformedData)

      dispatch(action)
        .then(result => {
          showToast({
            type: 'success',
          })
          if (!id && result?.id) {
            navigate(`/imobiliaria/${agencyId}/empreendimento/${result.id}`)
          }
        })
        .catch(() => {
          showToast({
            type: 'error',
          })
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [isValid, agencyId, id, deletedUnits]
  )

  useEffect(() => {
    if (buildingTypes.length === 0) {
      dispatch(getBuildingTypes())
    }
  }, [buildingTypes])

  useEffect(() => {
    if (id) {
      dispatch(getAgencyBuildingById(agencyId, id)).then(data => setBuildingData(data))
    }
  }, [id, agencyId])

  const fetchUnits = useCallback(() => {
    dispatch(getBuildingUnitsById(agencyId, id)).then(data => setUnits(data?.results))
  }, [agencyId, id])

  useEffect(() => {
    setUnits(buildingData?.units ?? [])
  }, [buildingData])

  useEffect(() => {
    if (agencyId) {
      dispatch(getAgencyFull(agencyId))
      dispatch(getCities())
      dispatch(getStates())
    }
  }, [agencyId])

  useEffect(() => {
    if (buildingData) {
      reset({
        name: buildingData.name || '',
        buildingType: buildingData.buildingType || '',
        businessType: buildingData.businessType || '',
        responsible: buildingData.responsible || '',
        phone: includeDDI(buildingData.phone) || '',
        emails: buildingData.emails || '',
        observations: buildingData.observations || '',
        active: buildingData.active ?? true,
        address: buildingData?.address || '',
        complement: buildingData?.complement || '',
        neighborhood: buildingData?.neighborhood || '',
        city: buildingData?.city || '',
        state: buildingData?.state || '',
        cep: buildingData?.cep || '',
        number: buildingData?.number || '',
        responsibleManager: buildingData?.responsibleManager || '',
        phoneManager: includeDDI(buildingData?.phoneManager) || '',
        emailsManager: buildingData?.emailsManager || '',
        cpfCnpjManager: buildingData?.cpfCnpjManager || '',
        isServiceOrderBuilding: buildingData.isServiceOrderBuilding ?? true,
      })
    }
  }, [buildingData, reset])

  const handleCancel = () => {
    if (Object.keys(dirtyFields).length > 0) {
      showDialog({
        type: 'warning',
        subject: 'Você tem certeza que deseja cancelar estas alterações?',
        labelApprove: 'Sim',
        labelCancel: 'Não',
        onApprove: () => {
          closeDialog()
          navigate(-1)
          reset()
        },
        onCancel: closeDialog,
      })
    } else {
      navigate(-1)
    }
  }

  const handleDelete = useCallback(unitToDelete => {
    setUnits(prevUnits => prevUnits.filter(unit => unit.id !== unitToDelete.id))
    setDeletedUnits(prev => [...prev, unitToDelete])
  })

  const buildingType = useMemo(() => {
    return buildingTypes.find(item => item?.id === buildingData?.buildingType)
  }, [buildingTypes, buildingData?.buildingType])

  return (
    <FormProvider {...methods}>
      <form className={styles.form} onSubmit={e => e.preventDefault()}>
        <HeaderTitle
          title={agency?.propertyTerm ? agency?.propertyTerm : 'Empreendimento'}
          backButtonAction={handleBackNavigation}
        >
          <Grid className={styles.headerButtons}>
            {isEditing && (
              <>
                <ReferaButton
                  className={styles.button}
                  onClick={handleCancel}
                  color="red"
                  variant="secondary"
                >
                  Cancelar
                </ReferaButton>
                <Button
                  className={styles.button}
                  onClick={handleSubmit(onSubmit)}
                  color="primary"
                  variant="contained"
                  disabled={isLoading || !isValid}
                >
                  Salvar
                </Button>
              </>
            )}
            {!isEditing && (
              <Button
                className={styles.button}
                onClick={setIsEditing}
                color="primary"
                variant="outlined"
                endIcon={<Svg icon={EditIcon} className={styles.editIcon} />}
              >
                Editar
              </Button>
            )}
          </Grid>
        </HeaderTitle>
        <Grid className={styles.container}>
          <BuildingData isEditing={isEditing} />
          <Contact isEditing={isEditing} />
          <ContactManager isEditing={isEditing} />
          <BuildingAddress isEditing={isEditing} />
          <Units
            buildingType={buildingType}
            units={units}
            isEditing={isEditing}
            onDelete={handleDelete}
            onSuccess={fetchUnits}
          />
          <Information isEditing={isEditing} />
        </Grid>
      </form>
    </FormProvider>
  )
}

export default AgencyBuilding
